<template>
    <div
        class="sl-order-form__line-side"
        :class="'sl-order-form__line-side-' + side"
    >
        <!-- Helper -->
        <div class="sl-order-form__line-band-wrapper">
            <div class="sl-order-form__line-band-content"></div>
        </div>

        <!-- Error Wrapper -->
        <div class="sl-order-form__line-content-wrapper">
            <!-- Field Wrapper -->
            <div class="sl-order-form__field-wrapper">
                <!-- Field -->
                <div class="sl-order-form__ofg">
                    <button
                        ref="autoFitBtn"
                        class="lsn-btn"
                        :class="calculating
                            ? 'lsn-btn--gray-outline lsn-btn--disabled'
                            : 'lsn-btn--primary-outline'"
                        :tabindex="tabIndex"
                        @click="autoFitBtn_onClick"
                        @keypress.enter="autoFitBtn_onClick"
                    >
                        <div v-if="calculating" class="w-4 h-4">
                            <img src="/img/loader.gif" alt="loading" />
                        </div>

                        <mdi-icon v-else icon="mdiAutoFix" class="w-4 h-4" />

                        {{ t('parameters.OFG_BUTTON.auto_fit.label') }}
                    </button>
                </div>
            </div>
        </div>

        <teleport to="body">
            <!-- Calculated Values Modal -->
            <lsn-modal :is-open="modalOpen" @keydown.esc="modalOpen = false">
                <div class="sl-prompt sl-prompt--info">
                    <!-- Content -->
                    <div class="sl-prompt__contents">
                        <!-- Title -->
                        <div class="sl-prompt__title">
                            {{ t('parameters.OFG_BUTTON.view_suggested_fitting.title') }}
                        </div>

                        <div class="sl-prompt__text">
                            <div v-if="loading" class="flex justify-center items-center">
                                <img src="/img/loader.gif" alt="loading" />
                            </div>

                            <div v-else-if="ofg.hasErrors()" class="text-red-600">
                                {{ t('parameters.OFG_BUTTON.view_suggested_fitting.errors') }}
                            </div>

                            <div v-else class="grid grid-cols-2 gap-2 text-sm">
                                <template
                                    v-for="param in suggested"
                                    :key="param.code"
                                >
                                    <template v-if="param.visible">
                                        <div class="flex items-center font-bold">
                                            <div class="mr-2">
                                                {{ param.label }}
                                            </div>

                                            <tippy v-if="param.help">
                                                <mdi-icon
                                                    icon="mdiInformationOutline"
                                                    class="cursor-help w-4 h-4 text-gray-400"
                                                />

                                                <template #content>
                                                    {{ param.help }}
                                                </template>
                                            </tippy>
                                        </div>

                                        <div class="text-primary-500">
                                            <span
                                                v-if="param.value === null ||
                                                    typeof param.value === 'undefined'"
                                                class="text-gray-400"
                                            >
                                                &empty;
                                            </span>

                                            <span v-else>
                                                {{ param.value }}
                                            </span>

                                            <span v-if="param.unit" class="ml-1 text-gray-500">
                                                {{ param.unit }}
                                            </span>
                                        </div>
                                    </template>
                                </template>
                            </div>
                        </div>
                    </div>

                    <!-- Modal Buttons -->
                    <div class="sl-prompt__actions">
                        <!-- Cancel -->
                        <button
                            ref="modalCancelBtn"
                            class="sl-prompt__button sl-prompt__button--white"
                            @click="modalOpen = false"
                        >
                            {{ t('common.cancel') }}
                        </button>

                        <!-- Copy -->
                        <button
                            v-tippy
                            :content="t('parameters.OFG_BUTTON.copy.help')"
                            class="sl-prompt__button relative"
                            :class="copyButtonClass"
                            @click="copyToClipboard"
                        >
                            <div>
                                {{ t('common.copy') }}
                            </div>
                        </button>

                        <!-- Attribute -->
                        <button
                            ref="modalAttributeBtn"
                            class="sl-prompt__button w-[162%]"
                            :class="disabled
                                ? 'sl-prompt__button--disabled'
                                : 'sl-prompt__button--primary'"
                            @click="attribute"
                        >
                            <div class="flex justify-center items-center gap-2 text-center">
                                <div v-if="attributing" class="w-4 h-4">
                                    <img src="/img/loader.gif" />
                                </div>

                                <div>{{ $t(`calculators.common.attribute_${side}`) }}</div>
                            </div>
                        </button>
                    </div>
                </div>
            </lsn-modal>
        </teleport>
    </div>
</template>

<script setup>
import { computed, nextTick, ref, watch } from 'vue';
import { useStore } from 'vuex';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import useOfgComposable from '@/composables/OfgComposable';
import { fieldProps, useFieldComposable } from '@/composables/FieldComposable';
import Fitting from '@/models/Fitting';
import FormPrototype from '@/models/FormPrototype';
import { pf } from '@/utils/vue-formatter';
import Parameter from '@/models/Parameter';
import LensOrderUrlManager from '@/views/order/lenses/LensOrderUrlManager';


// ------------------------------------------------------------ PROPS

const props = defineProps(fieldProps);


// ------------------------------------------------------------ EMITS

const emit = defineEmits(['close']);


// ------------------------------------------------------------ COMPOSABLES

const store = useStore();
const router = useRouter();
const route = useRoute();
const { t } = useI18n({ useScope: 'global' });
const { fittings, isOfgAccessible } = useOfgComposable();
const { tabIndex } = useFieldComposable(props, emit, store);


// ------------------------------------------------------------ REFS

const autoFitBtn = ref(null);
// const viewSuggestedFittingBtn = ref(null);
const modalCancelBtn = ref(null);
const modalAttributeBtn = ref(null);


// ------------------------------------------------------------ DATA

const loading = ref(false);
const calculating = ref(false);
const attributing = ref(false);
const modalOpen = ref(false);
/** @type {import("vue").Ref<Record<string, any>>} */
const results = ref({});
const copyOk = ref(false);


// ------------------------------------------------------------ COMPUTED

/** @type {import("vue").ComputedRef<Fitting>} */
const fitting = computed(() => fittings.value.get(props.side));
const ofgCode = computed(() => props.parameter.getValue());
const ofg = computed(() => new FormPrototype(
    store.getters['forms/getOfgByCode'](ofgCode.value)
));
const suggested = computed(() =>
{
    const out = [];
    for(const code in results.value)
    {
        const ofgParam = ofg.value.getParameterByCode(code);
        const param = fitting.value.getPrototype().getParameterByCode(code);
        const label = ofgParam?.form_label ?? param?.form_label ?? code;
        const help = ofgParam?.help ?? param?.help ?? null;
        let value = results.value[code];
        if(value === true || value === false)
        {
            value = t(value ? 'common.yes' : 'common.no');
        }
        else
        {
            value = pf(value, code, 'value');
        }

        out.push({
            code,
            label,
            help,
            value,
            unit:    param?.unit ?? '',
            visible: isVisible(param),
        });
    }

    return out;
});
const disabled = computed(() => attributing.value || loading.value ||
    calculating.value || ofg.value.hasErrors());
const copyButtonClass = computed(() =>
{
    if(copyOk.value)
    {
        return 'sl-prompt__button--green';
    }

    if(disabled.value)
    {
        return 'sl-prompt__button--disabled';
    }

    return 'sl-prompt__button--white';
});


// ------------------------------------------------------------ METHODS

async function attribute()
{
    if(disabled.value)
    {
        return;
    }

    attributing.value = true;
    try
    {
        const proto = fitting.value.getPrototype();
        const oldVariant = proto.variant_code;

        // Calculate the OFG
        await calculate();
        if(ofg.value.hasErrors())
        {
            return;
        }

        // Apply the values to the fitting
        proto.setValues(results.value);
        await store.dispatch('lensOrder/templateAndValidate', {
            side: props.side,
            options:
            {
                only_present: true,
                no_required:  true,
            },
        });

        // The modal might be open; close it.
        modalOpen.value = false;

        // Update the prototype if its variant changed
        const newVariant = results.value.LENS_VARIANT;
        if(newVariant && newVariant !== oldVariant)
        {
            updateVariant(newVariant);
        }
    }
    catch(error)
    {
        // @todo handle error
        console.error(error);
    }
    finally
    {
        attributing.value = false;
    }
}

/**
 * Update the
 * @param {string} newPrototypeCode
 */
function updateVariant(newVariant)
{
    const proto = fitting.value.getPrototype();
    const newProtoCode = `${proto.model_code}_${newVariant}`;
    const newProto = store.getters['prototypes/getByCode'](newProtoCode);
    store.commit('lensOrder/setPrototypeValue', {
        side:       props.side,
        paramCode:  'LENS_VARIANT',
        paramValue: newVariant,
    });

    const um = new LensOrderUrlManager(route);
    um.setPrototypeCode(props.side, newProtoCode);
    router.replace({ path: um.getPath() });
}

async function copyToClipboard()
{
    if(disabled.value)
    {
        return;
    }

    try
    {
        const data = suggested.value
            .filter(p => p.visible)
            .map(p => `${p.label}: ${p.value} ${p.unit}`.trim())
            .join('\n');

        await navigator.clipboard.writeText(data);
        copyOk.value = true;
        setTimeout(() =>
        {
            copyOk.value = false;
        }, 618);
    }
    catch(error)
    {
        // todo: handle error
        throw error;
    }
}

/**
 * Run the OFG calculations on the server, and update the `results` ref.
 */
async function calculate()
{
    if(calculating.value || !isOfgAccessible(props.side))
    {
        return;
    }

    calculating.value = true;

    // Template the OFG to get its parameter definitions
    const eid = store.state.account.cEntity.id;
    const viewAs = store.state.lensOrder.viewAs;
    try
    {
        await ofg.value.template(eid, {}, viewAs);
    }
    catch(error)
    {
        // @todo handle error
        calculating.value = false;
        return;
    }

    // Fill it with data from the current fitting
    const params = ofg.value.getParameters();
    for(const [code] of params)
    {
        const value = fitting.value.getPrototype().getValue(code);
        ofg.value.setValue(code, value);
    }

    // Calculate the OFG
    try
    {
        results.value = await ofg.value.calculateOfg(eid);
        ofg.value.clearErrors();
    }
    catch(error)
    {
        if(error === 'aborted')
        {
            return;
        }

        // Transfer errors to current fitting
        const errors = error?.response?.errors;
        if(errors)
        {
            results.value = {};
            fitting.value.getPrototype().setErrors(errors);
            return;
        }

        throw error;
    }
    finally
    {
        calculating.value = false;
    }
}

async function autoFitBtn_onClick()
{
    if(loading.value || attributing.value)
    {
        return;
    }

    await calculate();
    if(ofg.value.hasErrors())
    {
        return;
    }

    modalOpen.value = true;
}

watch(() => modalOpen.value, async() =>
{
    if(!modalOpen.value)
    {
        // On close: clear results and reset focus
        await nextTick();
        results.value = {};

        // viewSuggestedFittingBtn.value.focus();
        autoFitBtn.value.focus();
        return;
    }

    // Calculate and display results
    if(loading.value || attributing.value)
    {
        return;
    }

    loading.value = true;
    try
    {
        if(Object.keys(results.value).length === 0)
        {
            await calculate();
        }

        await nextTick();
        modalAttributeBtn.value.focus();
    }
    catch(error)
    {
        // @todo handle error
        console.error(error);
    }
    finally
    {
        loading.value = false;
    }
});

/**
 * Determine whether a parameter should be displayed.
 *
 * @param {Parameter} param
 * @returns {boolean}
 */
function isVisible(param)
{
    const display = param.form_splitted_display ?? param.form_display;
    return display !== 'HIDDEN';
}
</script>

<style scoped>
@reference "#main.css";

.sl-order-form__ofg
{
    @apply flex flex-row items-center gap-2;

    & > .lsn-btn:first-child
    {
        @apply inline-flex flex-row items-center gap-2;
    }
}

.sl-order-form__line-side-right .sl-order-form__ofg
{
    @apply flex-row-reverse;
}
</style>
